import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { companionService, Companion } from '../../services/companionService';
import { chatApi } from '../../services/chatApi';

import { ChatbotPersonality } from '../../types/chatbot';
import ChatInterface from '../ChatInterface';
import ConversationSidebar from '../ConversationSidebar';
import ChatbotProfile from '../ChatbotProfile';
import TopBar from '../TopBar';
import LoginModal from '../LoginModal';
import LoadingScreen from '../ui/LoadingScreen';

// Helper function to convert companion name to URL-safe slug
const nameToSlug = (name: string): string => {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

// Helper function to convert companion to chatbot personality
const convertCompanionToChatbotPersonality = (companion: Companion): ChatbotPersonality => {
  const newAssets = companion.assets;
  const primaryPhoto = newAssets?.primary_photo?.presigned_url;
  const primaryVideo = newAssets?.primary_video?.presigned_url;
  const gallery = newAssets?.gallery || [];

  let avatarUrl = '';
  let avatarType = 'image';
  let posterUrl = '';

  if (primaryPhoto) {
    avatarUrl = primaryPhoto;
    avatarType = 'image';
  } else if (primaryVideo) {
    avatarUrl = primaryVideo;
    avatarType = 'video';
    posterUrl = primaryPhoto || (gallery.find(asset => asset.type === 'photo')?.presigned_url || '');
  }

  // Create gallery images array (for backward compatibility)
  let galleryImages: string[] = [];
  if (newAssets && typeof newAssets === 'object' && !Array.isArray(newAssets)) {
    const allAssets = [];
    if (primaryPhoto) {
      allAssets.push(primaryPhoto);
    }
    // Add gallery photos
    gallery.filter(asset => asset.type === 'photo').forEach(asset => {
      allAssets.push(asset.presigned_url);
    });
    galleryImages = allAssets;
  }

  // Create galleryAssets array (includes both images and videos)
  const galleryAssets = [];
  
  // Add primary photo
  if (primaryPhoto) {
    galleryAssets.push({
      id: `primary_photo_${companion.companion_id}`,
      url: primaryPhoto,
      type: 'image' as const
    });
  }

  // Add primary video
  if (primaryVideo) {
    galleryAssets.push({
      id: `primary_video_${companion.companion_id}`,
      url: primaryVideo,
      type: 'video' as const,
      poster: primaryPhoto || (gallery.find(asset => asset.type === 'photo')?.presigned_url || undefined)
    });
  }

  // Add gallery assets
  gallery.forEach((asset, index) => {
    galleryAssets.push({
      id: asset.asset_id || `asset_${index}`,
      url: asset.presigned_url,
      type: asset.type === 'photo' ? 'image' as const : 'video' as const,
      poster: asset.type === 'video' ? (gallery.find(a => a.type === 'photo')?.presigned_url || undefined) : undefined
    });
  });

  let personalityDescription = '';
  if (companion.attributes) {
    const { physical, personal } = companion.attributes;
    const attributes = [];

    if (physical) {
      if (physical.ethnicity) attributes.push(physical.ethnicity);
      if (physical.eye_color) attributes.push(`${physical.eye_color} eyes`);
      if (physical.hair_color && physical.hair_style) {
        attributes.push(`${physical.hair_color} ${physical.hair_style} hair`);
      } else if (physical.hair_color) {
        attributes.push(`${physical.hair_color} hair`);
      } else if (physical.hair_style) {
        attributes.push(`${physical.hair_style} hair`);
      }
      if (physical.body_type) attributes.push(physical.body_type);
    }

    if (personal) {
      if (personal.occupation) attributes.push(personal.occupation);
      if (personal.hobby) attributes.push(`enjoys ${personal.hobby}`);
    }

    if (attributes.length > 0) {
      personalityDescription = attributes.join(', ');
    }
  }

  // Extract display name from new name structure
  const displayName = typeof companion.name === 'object' && companion.name
    ? (companion.name.nick_name || `${companion.name.first_name || ''} ${companion.name.last_name || ''}`.trim() || 'Unknown')
    : String(companion.name || 'Unknown');

  return {
    id: companion.companion_id,
    name: displayName,
    age: companion.age || 20,
    personality: typeof companion.personality === 'string' ? companion.personality : JSON.stringify(companion.personality || {}),
    description: companion.description || '',
    welcomeMessage: `Hello! I'm ${displayName}. Nice to meet you!`,
    systemPrompt: `You are ${displayName}, ${companion.description || 'a friendly AI companion'}. ${personalityDescription ? `You have ${personalityDescription}.` : ''}`,
    avatar: avatarUrl,
    avatarType: avatarType as 'image' | 'video',
    posterUrl: posterUrl,
    gallery: galleryImages,
    galleryAssets: galleryAssets, // Add the complete gallery assets
    attributes: companion.attributes ? {
      physical: companion.attributes.physical,
      personal: companion.attributes.personal ? {
        occupation: companion.attributes.personal.occupation,
        hobby: companion.attributes.personal.hobby?.join(', ') || undefined
      } : undefined
    } : undefined, // Convert hobby array to string
    mood: 'caring' as any,
  } as ChatbotPersonality;
};

const CompanionChat: React.FC = () => {
  const { companionName, companionId } = useParams<{ companionName: string; companionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [companion, setCompanion] = useState<Companion | null>(null);
  const [chatbot, setChatbot] = useState<ChatbotPersonality | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [chatbotStats, setChatbotStats] = useState({
    likeCount: 0,
    chatCount: 0,
    isLiked: false,
    hasStartedChat: true,
  });

  useEffect(() => {
    if (!companionId) {
      setError('Invalid companion ID');
      setLoading(false);
      return;
    }

    // Check for chat parameter in URL
    const chatId = searchParams.get('chat');
    if (chatId) {
      setConversationId(chatId);
    }

    loadCompanion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companionId, companionName, navigate, searchParams]);

  const loadCompanion = async () => {
    try {
      setLoading(true);
      
      // Load companion and chat data in parallel for faster loading
      const [companionResponse, chatData] = await Promise.allSettled([
        companionService.getAllCompanions(),
        conversationId ? chatApi.getChatMessages(conversationId) : Promise.resolve(null)
      ]);

      // Handle companion data
      if (companionResponse.status === 'rejected') {
        throw new Error('Failed to load companion');
      }

      const foundCompanion = companionResponse.value.companions.find(c => c.companion_id === companionId);
      
      if (!foundCompanion) {
        setError('Companion not found');
        return;
      }

      // Verify the name matches the URL (for SEO and consistency)
      const displayName = typeof foundCompanion.name === 'object' && foundCompanion.name
        ? (foundCompanion.name.nick_name || `${foundCompanion.name.first_name || ''} ${foundCompanion.name.last_name || ''}`.trim() || 'Unknown')
        : String(foundCompanion.name || 'Unknown');
      const expectedSlug = nameToSlug(displayName);
      if (companionName !== expectedSlug) {
        // Redirect to correct URL with chat parameter preserved
        const chatParam = conversationId ? `?chat=${conversationId}` : '';
        navigate(`/conversations/${expectedSlug}/${companionId}${chatParam}`, { replace: true });
        return;
      }

      setCompanion(foundCompanion);
      const chatbotPersonality = convertCompanionToChatbotPersonality(foundCompanion);
      setChatbot(chatbotPersonality);
      setError(null);

      // Pre-warm chat data if available
      if (chatData.status === 'fulfilled' && chatData.value) {
        console.log('✅ Chat data pre-loaded:', chatData.value.messages.length, 'messages');
      }
    } catch (err) {
      console.error('Failed to load companion:', err);
      setError('Failed to load companion');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToConversations = () => {
    navigate('/conversations');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Show loading screen while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <LoadingScreen
          title="Checking Authentication"
          message="Verifying your access..."
        />
      </div>
    );
  }

  // Only show sign in required after auth check is complete
  if (!user) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">🔒</div>
          <h1 className="text-3xl font-bold text-white mb-4">Sign In Required</h1>
          <p className="text-dark-300 mb-8">You need to sign in to chat with AI companions.</p>
          <div className="space-y-3">
            <button
              onClick={() => setShowLoginModal(true)}
              className="w-full px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
            >
              Sign In
            </button>
            <button
              onClick={handleBackToHome}
              className="w-full px-6 py-3 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-all duration-300"
            >
              Back to Home
            </button>
          </div>
        </div>

        {showLoginModal && (
          <LoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            initialMode="login"
          />
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <LoadingScreen
          title="Loading Companion"
          message="Setting up your chat experience..."
        />
      </div>
    );
  }

  if (error || !companion || !chatbot) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">😕</div>
          <h1 className="text-3xl font-bold text-white mb-4">Companion Not Found</h1>
          <p className="text-dark-300 mb-8">{error || 'The companion you\'re looking for doesn\'t exist.'}</p>
          <div className="space-y-3">
            <button
              onClick={handleBackToConversations}
              className="w-full px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
            >
              View All Conversations
            </button>
            <button
              onClick={handleBackToHome}
              className="w-full px-6 py-3 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-all duration-300"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-dark-900 flex flex-col overflow-hidden">
      {/* Use consistent TopBar */}
      <TopBar />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden pt-16 lg:pt-16 gap-4 p-4 pt-20 lg:pt-20 relative">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          <ChatInterface
            selectedChatbot={chatbot}
            conversationId={conversationId}
            onToggleProfile={() => setShowProfile(!showProfile)}
          />
        </div>

        {/* Right Sidebar - AI Profile - Desktop */}
        {showProfile && (
          <div className="hidden lg:block w-80 flex-shrink-0 transition-all duration-300">
            {chatbot && (
              <ConversationSidebar
                chatbot={chatbot}
                chatbotStats={chatbotStats}
                onUpdateStats={(stats) => setChatbotStats(prev => ({ ...prev, ...stats }))}
                onClose={() => setShowProfile(false)}
                onShowProfile={() => setShowFullProfile(true)}
              />
            )}
          </div>
        )}

        {/* Right Sidebar - AI Profile - Mobile (Overlay) */}
        {showProfile && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowProfile(false)}
            />
            <div className="fixed top-20 right-4 bottom-4 w-80 z-50 lg:hidden">
              {chatbot && (
                <ConversationSidebar
                  chatbot={chatbot}
                  chatbotStats={chatbotStats}
                  onUpdateStats={(stats) => setChatbotStats(prev => ({ ...prev, ...stats }))}
                  onClose={() => setShowProfile(false)}
                  onShowProfile={() => setShowFullProfile(true)}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Full Profile Modal */}
      {showFullProfile && chatbot && (
        <ChatbotProfile
          chatbot={chatbot}
          onBack={() => setShowFullProfile(false)}
          onStartChat={() => {
            // Already in chat, just close the profile
            setShowFullProfile(false);
          }}
          chatbotStats={chatbotStats}
          onUpdateStats={(stats) => setChatbotStats(prev => ({ ...prev, ...stats }))}
        />
      )}
    </div>
  );
};

export default CompanionChat;